import { createApp } from 'vue';
import { createPinia } from 'pinia';
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate';

import App from './App.vue';
import router from './router';
import i18n from './i18n';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

import API from './classes/API';
import { reloadOnceForChunkError } from './utility/chunkReload';
import { bootTrace } from './utility/bootTrace';

import * as Sentry from "@sentry/vue";

// si è scelto di importare bootstrap in main ts per poterlo gestire correttamente nello scope degli stili dei componenti
// altrimenti andrebbero in sovrapposizione con quelli applicati da WordPress
// import "bootstrap-italia/dist/css/bootstrap-italia.min.css";
// import "bootstrap-italia/dist/js/bootstrap-italia.bundle.min.js";

// plugins
// import installModals from "@/plugins/modals";

// Vite preload error: after a new deploy, old hashed chunks/CSS no longer exist.
// Force a page reload so the browser fetches the new assets — ma una sola volta: se
// l'asset è irraggiungibile per altri motivi, un reload incondizionato manda la pagina
// in loop. Se il reload è già stato tentato lasciamo propagare l'errore, così il router
// lo intercetta e App.vue mostra un messaggio azionabile.
window.addEventListener('vite:preloadError', (event) => {
	if (reloadOnceForChunkError()) {
		event.preventDefault();
	}
});

const app = createApp(App);
const pinia = createPinia();
pinia.use(piniaPluginPersistedstate);

app.use(pinia);
app.use(i18n);

// `app.use(router)` avvia da solo la navigazione iniziale: da qui in poi siamo dentro
// il percorso che, se non si chiude, lascia la pagina sul loader.
bootTrace('main: app.use(router) — avvio navigazione iniziale');
app.use(router);
router
	.isReady()
	.then(() => bootTrace('main: router PRONTO', router.currentRoute.value.fullPath))
	.catch((e) => bootTrace('main: router FALLITO', e));

// app.use(installModals);

// ignetto globalmente le dipendenze che mi serviranno, sicuramente
// all'interno dell'APP

// injection
const apiInstance = new API(import.meta.env.VITE_API_ENDPOINT);
app.provide('apiInstance', apiInstance);
//
app.component('FontAwesomeIcon', FontAwesomeIcon);

// Sentry
// se ho un environment specifico, inizializzo Sentry
if (import.meta.env.VITE_SENTRY_ENVIRONMENT) {
	Sentry.init({
		app,
		dsn: `https://229d4d3c5d98cd15a641c424473130f9@o4507368032370688.ingest.de.sentry.io/${import.meta.env.VITE_SENTRY_PROJECT_ID}`,
		environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
		// Set via VITE_APP_VERSION in CI/CD (e.g. git tag). Used to correlate errors to deploys.
		release: import.meta.env.VITE_APP_VERSION,
		integrations: [
			// Automatically tracks Vue Router navigation as transactions and breadcrumbs
			Sentry.browserTracingIntegration({ router }),
			Sentry.replayIntegration({
				maskAllText: false,
				maskAllInputs: false,
				blockAllMedia: false,
				networkDetailAllowUrls: [window.location.origin],
			}),
		],
		// parseFloat is required — env vars arrive as strings
		tracesSampleRate: parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || '0'),
		replaysSessionSampleRate: parseFloat(import.meta.env.VITE_SENTRY_REPLAYS_TRACES_SAMPLE_RATE || '0'),
		replaysOnErrorSampleRate: parseFloat(import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE || '1'),
		// Filter known browser noise before sending to Sentry
		beforeSend(event) {
			const ignoredMessages = [
				'ResizeObserver loop',
				'Non-Error promise rejection',
			];
			if (
				event.exception?.values?.some((v) =>
					ignoredMessages.some((m) => v.value?.includes(m))
				)
			) {
				return null;
			}
			return event;
		},
	});
}

// Capture unhandled errors thrown inside Vue component lifecycles / render
app.config.errorHandler = (err, _vm, info) => {
	if (import.meta.env.VITE_SENTRY_ENVIRONMENT) {
		Sentry.withScope((scope) => {
			scope.setTag('vue.lifecycle_hook', info);
			scope.setLevel('error');
			Sentry.captureException(err);
		});
	}
	console.error('[Vue errorHandler]', info, err);
};

bootTrace('main: app.mount()');
app.mount('#app');
bootTrace('main: app.mount() completato');